import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react"

import {
  Button,
  Col,
  Collapse,
  Drawer,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Typography,
  UploadFile,
  Skeleton,
  Alert,
  TimeRangePickerProps as AntDatePickerProps,
  Checkbox,
  message
} from "antd"
import dayjs from "dayjs"
import { useSearchParams } from "react-router-dom"
import { uid } from "uid"

import { DatePicker } from "@/components/_Shared/DatePicker"
import { NumericInput } from "@/components/_Shared/NumericInput"
import { PicturesUpload } from "@/components/_Shared/PicturesUpload"
import { CustomerContext } from "@/context/CustomerContext"
import { getCustomerClientsSelectList } from "@/http/customerClient"

import classes from "./DriverAddForm.module.scss"

import { useDriverByIdQuery } from "@/hook/Dictionaries/useDriverQuery"
import { useDriversMutation } from "@/hook/Dictionaries/useDriversMutation"

import {
  DriverCitizenRussianFederationForSelect,
  DriverTypeIdentityDocumentForSelect,
  IDocumentFile,
  IDriver,
  IDriverCreate,
  IDriverIsCitizenRussianFederation,
  IDriverTypeIdentityDocument
} from "@/types/IDriver"
import { ISelectListItem } from "@/types/ui"

import { PHONE_LENGTH } from "@/utils/constants"
import { uploadFileConvert } from "@/utils/uploadFileConvert"


const { Text } = Typography

export interface DriverAddFormProps {
  open(id?: number, isCustomerEdit?: boolean): void

  close(): void
}

interface DriverAddFormValues {
  fullName: string
  firstName: string
  lastName: string
  isCitizenRussian: number
  birthDate: string
  birthPlace: string
  patronymic: string
  passportNumber?: string
  passportSeries?: string
  passportIssuedBy?: string
  passportDivisionCode?: string
  reasonForIssue?: string
  decisionNumber?: string
  decisionDate?: string
  passportDate?: string
  validUntil?: string
  typeIdentityDocument?: number
  region: string
  district: string
  settlement: string
  street: string
  house: string
  building: string
  apartment: string
  phone: number
  passportScans?: IDocumentFile[]
  consentScans?: IDocumentFile[]
  type: IDriver
  clientIds: number[]

  passportScan: number
  consentScan: number
  adrScan?: number
  driverLicenseScan?: number
}

export const DriverAddForm = forwardRef<DriverAddFormProps>((props, ref) => {
  const customerContext = useContext(CustomerContext)

  const [form] = Form.useForm<DriverAddFormValues>()
  const [id, setId] = useState<number>(0)
  const [isOpen, setIsOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAgeValid, setIsAgeValid] = useState<boolean>(false)
  const [isAgeChosen, setIsAgeChosen] = useState<boolean>(false)
  const [birthdate] = useState<dayjs.Dayjs | null>(dayjs().subtract(18, "year"))
  const [passportSeries, setPassportSeries] = useState("")
  const [passportNumber, setPassportNumber] = useState("")
  const [isNoPatronymic, setIsNoPatronymic] = useState<boolean>(false)
  const [isCitizenRussianFederation, setIsCitizenRussianFederation] = useState<boolean>(true)

  const [hasPassportDivisionCode, setHasPassportDivisionCode] = useState(false)
  const [typeIdentityDocument, setTypeIdentityDocument] = useState<IDriverTypeIdentityDocument | undefined>(undefined)

  const [isCustomerEdit, setIsCustomerEdit] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState("")
  const [modalText, setModalText] = useState("")
  const [data, setData] = useState<DriverAddFormValues>()
  const [clients, setClients] = useState<ISelectListItem[]>([])

  const [searchParams, setSearchParams] = useSearchParams()

  const { data: driver, isLoading } = useDriverByIdQuery(id)
  const { createMutation, updateMutation, uploadScansMutation } = useDriversMutation()

  const [passportList, setPassportList] = useState<UploadFile[]>([])
  const [consentList, setConsentList] = useState<UploadFile[]>([])
  const [adrList, setAdrList] = useState<UploadFile[]>([])
  const [driverLicenseList, setDriverLicenseList] = useState<UploadFile[]>([])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, isNumber: boolean) => {
    if (typeIdentityDocument === IDriverTypeIdentityDocument.BelorussianPassport) {
      return
    }

    const digitsOnly = e.target.value.replace(/\D/g, "")
    if (isNumber){
      setPassportNumber(digitsOnly)
      form.setFieldValue("passportNumber", digitsOnly)
    }
    else {
      setPassportSeries(digitsOnly)
      form.setFieldValue("passportSeries", digitsOnly)
    }
  }

  useImperativeHandle(ref, () => ({
    open(id?: number, isCustomerEdit?: boolean) {
      onOpenHandler(id, isCustomerEdit)
    },
    close() {
      onCloseHandler()
    }
  }))

  const formDriverText = useMemo(() => {
    return id === 0 ? "Внести" : "Изменить"
  }, [id])

  useEffect(() => {
    if (driver?.response?.passportScans) {
      const newPromises = driver.response.passportScans.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromises).then((res) => {
        setPassportList(res)
        form.setFieldValue("passportScan", res.length === 0 ? undefined : res.length)
      })
    }

    if (driver?.response?.consentScans) {
      const newPromises = driver.response.consentScans.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromises).then((res) => {
        setConsentList(res)
        form.setFieldValue("consentScan", res.length === 0 ? undefined : res.length)
      })
    }

    const adrList = driver?.response?.adrScans ?? driver?.response?.adrScans
    if (adrList) {
      const newPromisesAdr = adrList.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromisesAdr).then((res) => {
        setAdrList(res)
        form.setFieldValue("adrScan", res.length === 0 ? undefined : res.length)
      })
    }

    const dlList = driver?.response?.driverLicenseScans ?? driver?.response?.driverLicenseScans
    if (dlList) {
      const newPromisesDL = dlList.map((file) =>
        uploadFileConvert({
          url: file.content,
          name: file.name,
          uid: uid()
        })
      )
      Promise.all(newPromisesDL).then((res) => {
        setDriverLicenseList(res)
        form.setFieldValue("driverLicenseScan", res.length === 0 ? undefined : res.length)
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.response.passportScans])

  useEffect(() => {
    if (isOpen && !customerContext.isCustomer()) {
      getCustomerClientsSelectList().then((res) => setClients(res.response))
    }

    if (driver && isOpen) {
      const response = driver.response
      setTypeIdentityDocument(response.identityDocument?.typeIdentityDocument)

      if (
        IDriverTypeIdentityDocument.RussianPassport ===
        response.identityDocument?.typeIdentityDocument
      ) {
        setHasPassportDivisionCode(true)
      }

      form.setFields([
        {
          name: "lastName",
          value: response.lastName
        },
        {
          name: "firstName",
          value: response.firstName
        },
        {
          name: "patronymic",
          value: response.patronymic
        },
        {
          name: "isCitizenRussian",
          value: response.isCitizenRussian ? 0 : 1
        },
        {
          name: "typeIdentityDocument",
          value: response.identityDocument?.typeIdentityDocument
        },
        {
          name: "birthDate",
          value:
            response.birthDate.length > 0
              ? dayjs(response.birthDate, "DD/MM/YYYY")
              : null
        },
        {
          name: "birthPlace",
          value: response.birthPlace
        },
        {
          name: "passportSeries",
          value: response.identityDocument?.passportSeries
        },
        {
          name: "passportNumber",
          value: response.identityDocument?.passportNumber
        },
        {
          name: "passportDivisionCode",
          value: response.identityDocument?.passportDivisionCode
        },
        {
          name: "passportIssuedBy",
          value: response.identityDocument?.passportIssuedBy
        },
        {
          name: "reasonForIssue",
          value: response.identityDocument?.reasonForIssue
        },
        {
          name: "decisionNumber",
          value: response.identityDocument?.decisionNumber
        },
        {
          name: "decisionDate",
          value:
            response.identityDocument?.decisionDate && response.identityDocument?.decisionDate?.length > 0 
              ? dayjs(response.identityDocument?.decisionDate, "DD/MM/YYYY")
              : null
        },
        {
          name: "passportDate",
          value:
            response.identityDocument?.passportDate && response.identityDocument?.passportDate?.length > 0 
              ? dayjs(response.identityDocument?.passportDate, "DD/MM/YYYY")
              : null
        },
        {
          name: "validUntil",
          value:
            response.identityDocument?.validUntil && response.identityDocument?.validUntil?.length > 0 
              ? dayjs(response.identityDocument?.validUntil, "DD/MM/YYYY")
              : null
        },
        {
          name: "region",
          value: response.region
        },
        {
          name: "district",
          value: response.district
        },
        {
          name: "settlement",
          value: response.settlement
        },
        {
          name: "street",
          value: response.street
        },
        {
          name: "house",
          value: response.house
        },
        {
          name: "building",
          value: response.building
        },
        {
          name: "apartment",
          value: response.apartment
        },
        {
          name: "phone",
          value: response.phone
        }
      ])

      form.setFieldValue("clientIds", response.clients.map(c => c.id))
      checkAgeDriver()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, driver, form, isOpen])

  const title = useMemo(() => {
    if (id) {
      return "Редактировать"
    }

    return "Добавить"
  }, [id])

  const setOrDeleteQueryParams = (name: string, value?: string) => {
    if (value) {
      searchParams.set(name, value)
    } else { 
      searchParams.delete(name)
    }

    setSearchParams(searchParams)
  }

  const checkAgeDriver = () =>{
    var date = form.getFieldValue("birthDate")
    if (date) {
      const birthday = new Date(dayjs(date).format("YYYY-MM-DD"))
      const today = new Date()
      const age = today.getFullYear() - birthday.getFullYear()

      if (age < 18) {
        setIsAgeValid(false)
      }
      else {
        setIsAgeValid(true)
      }

      setIsAgeChosen(true)
    }
  }

  const disabledDate: AntDatePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().subtract(18, "year").endOf("day")
  }

  const onOpenHandler = (id?: number, isCustomerEdit?: boolean) => {
    if (id) {
      setId(id)
    }

    setIsCustomerEdit(isCustomerEdit ?? false)
    form.resetFields()
    setIsOpen(true)
    setOrDeleteQueryParams("driverAddForm", "true")
  }
  
  const tryDriver = async (data: DriverAddFormValues | undefined) => {
    try {
      if (data === undefined) {
        return
      }

      const request: IDriverCreate = {
        lastName: data.lastName,
        firstName: data.firstName,
        patronymic: data.patronymic,
        fullName: data.fullName,
        isCitizenRussian: data.isCitizenRussian === 0,
        typeIdentityDocument: data.typeIdentityDocument,
        birthDate: data.birthDate,
        birthPlace: data.birthPlace,
        passportSeries: data.passportSeries?.toString(),
        passportNumber: data.passportNumber?.toString(),
        passportIssuedBy: data.passportIssuedBy,
        passportDate: data.passportDate,
        passportDivisionCode: data.passportDivisionCode,
        reasonForIssue: data.reasonForIssue,
        decisionDate: data.decisionDate,
        decisionNumber: data.decisionNumber,
        validUntil: data.validUntil,
        region: data.region,
        district: data.district,
        settlement: data.settlement,
        street: data.street,
        house: data.house,
        building: data.building,
        apartment: data.apartment,
        phone: data.phone?.toString(),
        approved: true,
        clientIds: customerContext.isCustomer()
            ? customerContext.customer?.customerClientId
              ? [customerContext.customer?.customerClientId]
              : []
            : data.clientIds
      }

      const passportScans = passportList.map((file) => ({
        content: (file.url as string).split("base64,")[1],
        name: file.name
      }))
      const consentScans = consentList.map((file) => ({
        content: (file.url as string).split("base64,")[1],
        name: file.name
      }))
      const adrScans = adrList.map((file) => ({
        content: (file.url as string).split("base64,")[1],
        name: file.name
      }))
      const driverLicenseScans = driverLicenseList.map((file) => ({
        content: (file.url as string).split("base64,")[1],
        name: file.name
      }))

      setIsProcessing(true)

      if (id) {
        if (isCustomerEdit) {
          await uploadScansMutation.mutateAsync({
            passportScans,
            consentScans,
            adrScans,
            driverLicenseScans: driverLicenseScans,
            driverId: Number(id)
          })
        } else {
          request.passportScans = passportScans
          request.consentScans = consentScans
          request.adrScans = adrScans
          request.driverLicenseScans = driverLicenseScans
          await updateMutation.mutateAsync({
            ...request,
            id: id
          })
        }
      } else {
        await createMutation.mutateAsync(request).then(response => {
          if (response.succeeded)
            uploadScansMutation.mutateAsync({
              passportScans,
              consentScans,
              adrScans,
              driverLicenseScans: driverLicenseScans,
              driverId: Number(response.response)
            })
        })
      }

      form.resetFields()
      onCloseHandler()
    } finally {
      setIsProcessing(false)
    }
  }

  const onFinish = (data: DriverAddFormValues) => {
    setData(data)
    setModalTitle("Предупреждение")
    setModalText(`Просим Вас проверить данные согласно паспорту, в случае если данные указаны не корректно или не соответствуют данным в паспорте –
    в записи на нефтебазу будет отказано!`)
    setIsModalOpen(true)
  }

  const onFinishFailed = ({ errorFields }: any) => {
    message.warning("Заполните все обязательные поля")

    if (errorFields.length > 0) {
      form.scrollToField(errorFields[0].name, {
        behavior: "smooth",
        block: "center"
      })
    }
  }

  const handleOk = async () => {
    setIsModalOpen(false)
    await tryDriver(data)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const onPassportListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setPassportList(file)
      form.setFieldValue("passportScan", file.length === 0 ? undefined : file.length)
    } else {
      const newList = [...passportList, file]
      setPassportList(newList)
      form.setFieldValue("passportScan", newList.length === 0 ? undefined : newList.length)
    }
  }

  const onConsentListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setConsentList(file)
      form.setFieldValue("consentScan", file.length === 0 ? undefined : file.length)
    } else {
      const newList = [...consentList, file]
      setConsentList(newList)
      form.setFieldValue("consentScan", newList.length === 0 ? undefined : newList.length)
    }
  }

  const onAdrListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setAdrList(file)
      form.setFieldValue("adrScan", file.length === 0 ? undefined : file.length)
    } else {
      const newList = [...adrList, file]
      setAdrList(newList)
      form.setFieldValue("adrScan", newList.length === 0 ? undefined : newList.length)
    }
  }

  const onDriverLicenseListChange = (file: UploadFile | UploadFile[]) => {
    if (Array.isArray(file)) {
      setDriverLicenseList(file)
      form.setFieldValue("driverLicenseScan", file.length === 0 ? undefined : file.length)
    } else {
      const newList = [...driverLicenseList, file]
      setDriverLicenseList(newList)
      form.setFieldValue("driverLicenseScan", newList.length === 0 ? undefined : newList.length)
    }
  }

  const onIdentityDocumentChange = (identityDocument: number) => {
    setTypeIdentityDocument(identityDocument)
    if (IDriverTypeIdentityDocument.RussianPassport === identityDocument) {
      setHasPassportDivisionCode(true)
    } else {
      form.setFieldValue("passportDivisionCode", undefined)
      setHasPassportDivisionCode(false)
    }
  }

  const onCloseHandler = () => {
    setId(0)
    setIsCustomerEdit(false)
    setIsNoPatronymic(false)
    setIsCitizenRussianFederation(true)
    form.resetFields()
    setPassportList([])
    setConsentList([])
    setAdrList([])
    setDriverLicenseList([])
    setIsOpen(false)
    setHasPassportDivisionCode(false)
    setOrDeleteQueryParams("driverAddForm")
  }

  return (
    <Drawer
      title={title}
      width={820}
      onClose={onCloseHandler}
      open={isOpen}
      maskClosable={false}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Button type="primary" disabled={isProcessing} onClick={form.submit}>
          {formDriverText}
        </Button>
      }
    >
      <Modal
        title={modalTitle}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Text>{ modalText }</Text>
      </Modal>
      <Skeleton loading={id === 0 ? false : isLoading}>
        <Form 
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          {
            !isCustomerEdit &&
            <>
              <Form.Item
                hidden={customerContext.isCustomer()}
                name="clientIds"
                label="Клиент"
                rules={[
                  {
                    required: !customerContext.isCustomer(),
                    message: "Пожалуйста выберите клиента"
                  }
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Клиент"
                  showSearch
                  options={clients}
                />
              </Form.Item>
              <Form.Item
                label="Фамилия"
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста введите Фамилия"
                  }
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Имя"
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста введите Имя"
                  }
                ]}
              >
                <Input />
              </Form.Item>
              <Row gutter={16}>
                <Col span={18}>
                  <Form.Item
                    label="Отчество"
                    name="patronymic"
                    rules={[
                      {
                        required: (!isNoPatronymic && isCitizenRussianFederation),
                        message: "Пожалуйста введите Отчество"
                      }
                    ]}
                  >
                    <Input disabled={isNoPatronymic} />
                  </Form.Item>
                </Col>
                <Col span={6} style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox checked={isNoPatronymic} onChange={(e)=> setIsNoPatronymic(e.target.checked)}>Нет отчества</Checkbox>
                </Col>
              </Row>
              <Form.Item
                name="isCitizenRussian"
                label="Является гражданином РФ"
                rules={[
                  {
                    required: true,
                    message:
                      "Пожалуйста выберите является ли водитель гражданином РФ"
                  }
                ]}
              >
                <Select
                  placeholder="Является гражданином РФ"
                  showSearch
                  options={DriverCitizenRussianFederationForSelect.map((type) => ({
                    value: type.id,
                    label: type.value
                  }))}
                  onChange={(value, option) => {
                    let newValue: IDriverIsCitizenRussianFederation
                    if (Array.isArray(option)) {
                      newValue = option[0].value
                    } else {
                      newValue = option?.value ?? 0
                    }

                    if (newValue === IDriverIsCitizenRussianFederation.No){
                      setIsCitizenRussianFederation(false)
                    }
                    else {
                      setIsCitizenRussianFederation(true)
                    }
                  }}
                /></Form.Item>
                <Form.Item
                  name="typeIdentityDocument"
                  label="Тип документа, удостоверяющего личность"
                  rules={[
                    {
                      required: true,
                      message:
                        "Пожалуйста выберите тип документа, удостоверяющего личность"
                    }
                  ]}
                >
                  <Select
                    onChange={onIdentityDocumentChange}
                    placeholder="Тип документа, удостоверяющего личность"
                    showSearch
                    options={DriverTypeIdentityDocumentForSelect.map((type) => ({
                      value: type.id,
                      label: type.value
                    }))}
                  />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Дата рождения"
                      name="birthDate"
                      rules={[
                        {
                          required: true,
                          message: "Пожалуйста введите дату рождения"
                        }
                      ]}
                    >
                      <DatePicker
                        defaultPickerValue={birthdate ? dayjs(birthdate) : undefined}
                        onChange={checkAgeDriver}
                        disabledDate={disabledDate}
                        placeholder="Дата рождения"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    {( isAgeChosen && !isAgeValid) && <Alert message="Водитель должен быть старше 18 лет" type="warning" showIcon />}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="birthPlace"
                      label="Место рождения"
                      rules={[
                        {
                          required: true,
                          message: "Пожалуйста введите место рождения"
                        }
                      ]}
                    >
                      <Input placeholder="Место рождения" />
                    </Form.Item>
                  </Col>
                </Row>
                {typeIdentityDocument !== undefined && (
                <Collapse
                  className={classes["collapse-driver"]}
                  defaultActiveKey={["1"]}
                  ghost
                  items={[
                    {
                      key: "1",
                      label: DriverTypeIdentityDocumentForSelect.find(x=>x.id === Number(typeIdentityDocument))?.value ?? "-",
                      children: (
                        <>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={!(typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport 
                                  || typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit)}
                                name="passportSeries"
                                label="Серия"
                                rules={[
                                  {
                                    required: typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport 
                                      || typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit,
                                    message: "Пожалуйста введите серию паспорта"
                                  }
                                ]}
                              >
                                <Input
                                  style={{ width: "100%" }}
                                  placeholder="Серия"
                                  maxLength={4}
                                  value={passportSeries}
                                  onChange={(e)=> handleInputChange(e, false)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={!(typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport 
                                  || typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit
                                  || typeIdentityDocument === IDriverTypeIdentityDocument.BelorussianPassport)}
                                name="passportNumber"
                                label="Номер"
                                rules={[
                                  {
                                    required:  typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport 
                                      || typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit
                                      || typeIdentityDocument === IDriverTypeIdentityDocument.BelorussianPassport,
                                    message: "Пожалуйста введите номер паспорта"
                                  }
                                ]}
                              >
                                <Input
                                  style={{ width: "100%" }}
                                  placeholder="Номер"
                                  value={passportNumber}
                                  maxLength={typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport ? 6 
                                    : typeIdentityDocument === IDriverTypeIdentityDocument.BelorussianPassport ? 14 : 7}
                                  onChange={(e)=> handleInputChange(e, true)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={!hasPassportDivisionCode}
                                name="passportDivisionCode"
                                label="Код подразделения"
                                normalize={(value) => value.toString()}
                                rules={
                                  !hasPassportDivisionCode
                                    ? []
                                    : [
                                      {
                                        required:  typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport,
                                        message:
                                          "Пожалуйста введите код подразделения"
                                      },
                                      {
                                        pattern: /^\d{3}-\d{3}$/,
                                        message: "Неверный формат, код должен быть вида: NNN-NNN"
                                      }
                                    ]
                                }
                              >
                                <Input
                                  style={{ width: "100%" }}
                                  placeholder="Код подразделения"
                                  min={0}
                                  maxLength={7}
                                  step={1}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="passportIssuedBy"
                                label="Выдан"
                                rules={[
                                  {
                                    required: true,
                                    message: "Пожалуйста введите кем выдан паспорт"
                                  }
                                ]}
                              >
                                <Input placeholder="Выдан" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="passportDate"
                                label="Дата выдачи"
                                rules={[
                                  {
                                    required: true,
                                    message: "Пожалуйста выберите дату выдачи"
                                  }
                                ]}
                              >
                                <DatePicker
                                  style={{ width: "100%" }}
                                  placeholder="Дата выдачи"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={typeIdentityDocument !== IDriverTypeIdentityDocument.RussianResidencePermit}
                                name="decisionNumber"
                                label="Номер решения"
                                rules={[
                                  {
                                    required: typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit,
                                    message: "Пожалуйста введите номер решения"
                                  }
                                ]}
                              >
                                <Input placeholder="Номер решения" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={typeIdentityDocument !== IDriverTypeIdentityDocument.RussianResidencePermit}
                                name="decisionDate"
                                label="Дата решения"
                                rules={[
                                  {
                                    required: typeIdentityDocument === IDriverTypeIdentityDocument.RussianResidencePermit,
                                    message: "Пожалуйста выберите дату решения"
                                  }
                                ]}
                              >
                                <DatePicker
                                  style={{ width: "100%" }}
                                  placeholder="Дата решения"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                              hidden={typeIdentityDocument !== IDriverTypeIdentityDocument.RussianTemporaryIdentityCard}
                                name="reasonForIssue"
                                label="Основание выдачи"
                                rules={[
                                  {
                                    required: typeIdentityDocument === IDriverTypeIdentityDocument.RussianTemporaryIdentityCard,
                                    message: "Пожалуйста введите Основание выдачи"
                                  }
                                ]}
                              >
                                <Input placeholder="Основание выдачи" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                hidden={typeIdentityDocument === IDriverTypeIdentityDocument.RussianPassport}
                                name="validUntil"
                                label="Срок действия до"
                                rules={[
                                  {
                                    required: typeIdentityDocument === IDriverTypeIdentityDocument.RussianTemporaryIdentityCard
                                      || typeIdentityDocument === IDriverTypeIdentityDocument.BelorussianPassport,
                                    message: "Пожалуйста выберите дату срока действия"
                                  }
                                ]}
                              >
                                <DatePicker
                                  style={{ width: "100%" }}
                                  placeholder="Срок действия до"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )
                    }
                  ]}
                ></Collapse>
                )}
                <Collapse
                  className={classes["collapse-driver"]}
                  defaultActiveKey={["1"]}
                  ghost
                  items={[
                    {
                      key: "1",
                      label: "Место жительства по прописке",
                      children: (
                        <>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item name="region" label="Регион"
                              rules={[
                                {
                                  required: true,
                                  message: "Пожалуйста введите регион"
                                }
                                ]}>
                                <Input placeholder="Регион" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="district"
                                label="Район"
                                rules={[
                                  {
                                    required: false,
                                    message: "Пожалуйста введите район"
                                  }
                                  ]}>
                                <Input placeholder="Район" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item name="settlement" label="Населенный пункт"
                              rules={[
                                {
                                  required: true,
                                  message: "Пожалуйста введите населенный пункт"
                                }
                                ]}>
                                <Input placeholder="Населенный пункт" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item name="street" label="Улица"
                              rules={[
                                {
                                  required: true,
                                  message: "Пожалуйста введите улицу"
                                }
                                ]}>
                                <Input placeholder="Улица" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item name="house" label="Дом"
                              rules={[
                                {
                                  required: true,
                                  message: "Пожалуйста введите номер дома"
                                }
                                ]}>
                                <Input placeholder="Дом" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item name="building" label="Корпус">
                                <Input placeholder="Корпус" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item name="apartment" label="Квартира"
                              rules={[
                                {
                                  required: false,
                                  message: "Пожалуйста введите номер квартиры"
                                }
                                ]}>
                                <Input placeholder="Квартира" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name="phone"
                                label="Номер телефона"
                                rules={[
                                  {
                                    required: true,
                                    message: "Пожалуйста введите номер телефона"
                                  }
                                ]}
                              >
                                <NumericInput
                                  type="number"
                                  prefix="+7"
                                  style={{ width: "100%" }}
                                  min={0}
                                  step={1}
                                  maxLength={PHONE_LENGTH}
                                  placeholder="Номер телефона"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )
                    }
                  ]}
                ></Collapse>
            </>
          }

          <Form.Item
            name="passportScan"
            label="Скан паспорта с пропиской"
            rules={[
              {
                required: true,
                message: "Пожалуйста загрузите скан паспорта с пропиской"
              }
            ]}
          >
            <PicturesUpload
              id="passportScan"
              onChangeHandler={onPassportListChange}
              fileList={passportList}
            />
          </Form.Item>
          <Form.Item
            name="consentScan"
            label="Скан согласия на обработку персональных данных"
            rules={[
              {
                required: true,
                message: "Пожалуйста загрузите скан согласия на обработку персональных данных"
              }
            ]}
          >
            <PicturesUpload
              id="consentScan"
              onChangeHandler={onConsentListChange}
              fileList={consentList}
            />
          </Form.Item>
          <Form.Item
            name="adrScan"
            label="ДОПОГ"
            rules={[
              {
                required: true,
                message: "Пожалуйста загрузите скан ДОПОГ"
              }
            ]}
          >
            <PicturesUpload
              id="adrScan"
              onChangeHandler={onAdrListChange}
              fileList={adrList}
            />
          </Form.Item>
          <Form.Item
            name="driverLicenseScan"
            label="Скан водительского удостоверения"
            rules={[
              {
                required: true,
                message: "Пожалуйста загрузите скан водительского удостоверения"
              }
            ]}
          >
            <PicturesUpload
              id="driverLicenseScan"
              onChangeHandler={onDriverLicenseListChange}
              fileList={driverLicenseList}
            />
          </Form.Item>
        </Form>
      </Skeleton>
    </Drawer>
  )
})